import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useQuery, useQueryClient } from "react-query";
import {
  Box,
  Typography,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Container,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import Candidates from "../../Candidates/Candidates";
import Spinner from "../../../UI/Spinner/Spinner";
import Pagination from "../../../UI/Pagination/Pagination";
import { LinkToJobIcon } from "../../../Assets/CustomIcons/CustomIconsList";

const SORT_OPTIONS = [
  { value: "last_added", label: "התווספו למערכת" },
  { value: "last_answered", label: "ענו על שאלון" },
];
const PER_PAGE = 20;
const LINK_ALL_LIMIT = 50;

const fetchMatchSuggestions = async ({ queryKey }) => {
  const [, jobId, page, sort, expandRange, acceptedCityOnly] = queryKey;
  const res = await axios.get(
    `/wp-json/api/v2/jobs/${jobId}/match_suggestions`,
    {
      params: {
        page,
        per_page: PER_PAGE,
        sort,
        expand_range: expandRange ? "true" : "false",
        require_accepted_city: acceptedCityOnly ? "true" : "false",
      },
    },
  );
  return {
    candidates: res.data,
    total: parseInt(res.headers["x-wp-total"] || "0", 10),
    totalPages: parseInt(res.headers["x-wp-totalpages"] || "0", 10),
    matchReason: res.headers["x-match-reason"] || null,
  };
};

const JobMatchSuggestions = ({ jobId, onFiltersChange }) => {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("last_answered");
  const [expandRange, setExpandRange] = useState(false);
  const [acceptedCityOnly, setAcceptedCityOnly] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [linkAllDialogOpen, setLinkAllDialogOpen] = useState(false);
  const [linkAllInFlight, setLinkAllInFlight] = useState(false);
  const queryClient = useQueryClient();

  // Report the checkbox state upwards so the ATS export code in the job
  // header ("העתק לייצוא רשימה") describes the list actually on screen.
  // Runs on mount too, which resets the parent when the tab is re-entered.
  useEffect(() => {
    if (onFiltersChange) onFiltersChange({ expandRange, acceptedCityOnly });
  }, [expandRange, acceptedCityOnly, onFiltersChange]);

  const { data, isLoading, error } = useQuery(
    ["jobMatchSuggestions", jobId, page, sort, expandRange, acceptedCityOnly],
    fetchMatchSuggestions,
    { keepPreviousData: true, staleTime: 60000, retry: 1, enabled: jobId > 0 },
  );

  const handleInvite = useCallback(
    async (candidateId) => {
      try {
        await axios.post(`/wp-json/api/v2/link_multiples/${jobId}`, {
          candidateIds: [candidateId],
          authorId: localStorage.getItem("userId"),
          source: "libi_match",
        });
        setSnackbar({
          severity: "success",
          message: "המועמד שויך למשרה",
        });
        queryClient.invalidateQueries(["jobMatchSuggestions", jobId]);
      } catch (err) {
        setSnackbar({
          severity: "error",
          message: "שיוך המועמד נכשל. אנא נסו שנית.",
        });
      }
    },
    [jobId, queryClient],
  );

  // Called by the <Candidates> bulk "שייך למשרה" flow after it links the
  // selected candidates. Refetch so the freshly-linked candidates drop out of
  // the suggestion list (the backend excludes candidates already linked).
  const handleBulkLinked = useCallback(() => {
    setSnackbar({
      severity: "success",
      message: "המועמדים שויכו למשרה",
    });
    queryClient.invalidateQueries(["jobMatchSuggestions", jobId]);
  }, [jobId, queryClient]);

  const handlePageChange = (_event, value) => setPage(value);

  const handleSortChange = (event) => {
    setSort(event.target.value);
    setPage(1);
  };

  const handleExpandRangeChange = (event) => {
    setExpandRange(event.target.checked);
    setPage(1);
  };

  const handleAcceptedCityOnlyChange = (event) => {
    setAcceptedCityOnly(event.target.checked);
    setPage(1);
  };

  const handleConfirmLinkAll = async () => {
    setLinkAllDialogOpen(false);
    setLinkAllInFlight(true);
    try {
      // Pull the top LINK_ALL_LIMIT IDs in a single request so the link
      // batch matches the dialog copy regardless of which page the user
      // is currently viewing.
      const res = await axios.get(
        `/wp-json/api/v2/jobs/${jobId}/match_suggestions`,
        {
          params: {
            page: 1,
            per_page: LINK_ALL_LIMIT,
            sort,
            expand_range: expandRange ? "true" : "false",
            require_accepted_city: acceptedCityOnly ? "true" : "false",
          },
        },
      );
      const ids = (res.data || []).map((c) => c.id).filter((id) => id > 0);
      if (ids.length === 0) {
        setSnackbar({
          severity: "info",
          message: "לא נמצאו מועמדים לשיוך.",
        });
        return;
      }
      await axios.post(`/wp-json/api/v2/link_multiples/${jobId}`, {
        candidateIds: ids,
        authorId: localStorage.getItem("userId"),
        source: "libi_match",
      });
      setSnackbar({
        severity: "success",
        message: `${ids.length} מועמדים שויכו למשרה`,
      });
      queryClient.invalidateQueries(["jobMatchSuggestions", jobId]);
    } catch (err) {
      setSnackbar({
        severity: "error",
        message: "שיוך כל המועמדים נכשל. אנא נסו שנית.",
      });
    } finally {
      setLinkAllInFlight(false);
    }
  };

  if (isLoading) return <Spinner open />;

  if (error) {
    return (
      <Box my={4}>
        <Alert severity="error">
          שגיאה בטעינת מועמדים מתאימים. אנא רעננו את העמוד.
        </Alert>
      </Box>
    );
  }

  if (data && data.matchReason === "no_filters_on_job") {
    return (
      <Container maxWidth="md">
        <Box my={4} textAlign="center">
          <Typography variant="h6">המשרה עדיין לא מכילה דרישות.</Typography>
          <Typography variant="body1" style={{ marginTop: 8 }}>
            הוסיפו תנאי סינון למשרה כדי לראות מועמדים מתאימים מליבי.
          </Typography>
        </Box>
      </Container>
    );
  }

  const total = (data && data.total) || 0;
  const totalPages = (data && data.totalPages) || 0;
  const candidates = (data && data.candidates) || [];
  const exceedsLimit = total > LINK_ALL_LIMIT;

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        my={2}
      >
        <Typography variant="h6">
          {`נמצאו ${total.toLocaleString("he-IL", {
            minimumFractionDigits: 0,
          })} מועמדים מתאימים`}
        </Typography>
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <Tooltip title="הרחבת טווח החיפוש מעבר לשישה חודשים אחרונים">
            <FormControlLabel
              control={
                <Checkbox
                  checked={expandRange}
                  onChange={handleExpandRangeChange}
                  color="primary"
                />
              }
              label="הרחבת טווח חיפוש"
            />
          </Tooltip>
          <Tooltip title="הצג רק מועמדים שאישרו במפורש שיעבדו בעיר של המשרה">
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptedCityOnly}
                  onChange={handleAcceptedCityOnlyChange}
                  color="primary"
                />
              }
              label="מיקום מדויק בלבד"
            />
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            disabled={total === 0 || linkAllInFlight}
            onClick={() => setLinkAllDialogOpen(true)}
          >
            שייך את כל המועמדים
          </Button>
          <FormControl
            size="small"
            variant="outlined"
            style={{ minWidth: 220 }}
          >
            <InputLabel id="job-match-sort-label">מיון</InputLabel>
            <Select
              labelId="job-match-sort-label"
              value={sort}
              label="מיון"
              onChange={handleSortChange}
            >
              {SORT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {total === 0 ? (
        <Box my={4} textAlign="center">
          <Typography variant="body1">
            לא נמצאו מועמדים מתאימים לפי דרישות המשרה.
          </Typography>
        </Box>
      ) : (
        <React.Fragment>
          <Pagination
            onChange={handlePageChange}
            totalPages={totalPages}
            total={total}
            totalLabel='סה"כ מועמדים:'
            currentPage={page}
          />
          <Candidates
            disableDefaultSearch
            candidates={{ data: candidates }}
            job={{ id: jobId }}
            implicitJobId={jobId}
            linkSource="libi_match"
            onCloseMultipleLinkToJob={handleBulkLinked}
            rowAction={{
              label: "שייך למשרה",
              icon: <LinkToJobIcon />,
              onClick: handleInvite,
            }}
          />
          <Pagination
            onChange={handlePageChange}
            totalPages={totalPages}
            total={total}
            totalLabel='סה"כ מועמדים:'
            currentPage={page}
          />
        </React.Fragment>
      )}

      <Dialog
        open={linkAllDialogOpen}
        onClose={() => setLinkAllDialogOpen(false)}
        aria-labelledby="link-all-title"
      >
        <DialogTitle id="link-all-title">שיוך כל המועמדים</DialogTitle>
        <DialogContent>
          {exceedsLimit && (
            <Alert severity="warning" style={{ marginBottom: 12 }}>
              {`קיימת הגבלה של ${LINK_ALL_LIMIT} מועמדים לפעולה זו. מתוך ${total.toLocaleString(
                "he-IL",
              )} מועמדים מתאימים, יישוייכו ${LINK_ALL_LIMIT} הראשונים בלבד (לפי המיון הנוכחי).`}
            </Alert>
          )}
          <DialogContentText>
            {exceedsLimit
              ? `לשייך את ${LINK_ALL_LIMIT} המועמדים הראשונים למשרה?`
              : `לשייך את כל ${total.toLocaleString("he-IL")} המועמדים למשרה?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkAllDialogOpen(false)}>בטל</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirmLinkAll}
            autoFocus
          >
            אישור
          </Button>
        </DialogActions>
      </Dialog>

      {snackbar && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setSnackbar(null)}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default JobMatchSuggestions;
